/**
 * King Protein — welcome offer engine (Phase 1004).
 *
 * One Alpine store ('welcome') coordinates the four capture surfaces (popup,
 * bottom sticky bar, homepage band, footer). One data component
 * ('kpWelcomeSurface') posts the native {% form 'customer' %} via fetch and
 * reveals the discount code on screen.
 *
 * This file is the ONLY place the code literal exists (D-02). It is assigned to
 * the store on a successful submit and rendered with x-text. Never write it to
 * markup, a data attribute or a translation string. All rendering is x-text /
 * store mutation; nothing here builds markup from strings (T-1004-02).
 * Storefront boundary: no Admin API call and no token (T-1004-04). The only
 * network call is a same-origin POST to the customer form action.
 *
 * Load order: layout/theme.liquid loads this file with defer immediately before the Alpine
 * script tag. Deferred scripts run in document order, so the alpine:init listener below is
 * registered before Alpine starts.
 *
 * Captcha: the store's customer form is protected by hCaptcha. A tokenless fetch is rejected
 * (HTTP 400/403), so each welcome form gets an instance-level submit() (see init() of the
 * surface). Shopify binds a form on first focus and captures that function, so its wrapper
 * ends in send() with the token already in the form. If anything goes wrong the wrapper does
 * a native POST with a token and init() of the store reveals the code after the reload.
 */
document.addEventListener('alpine:init', () => {
  if (!window.Alpine || window.__kpWelcomeReg) return;
  window.__kpWelcomeReg = true;

  const KEY = 'kp_welcome';
  const SEEN_KEY = 'kp_welcome_seen';
  const POSTED_KEY = 'kp_welcome_posted';
  const POPUP_SUPPRESS_DAYS = 30;
  const DAY_MS = 864e5;
  const POPUP_DELAY_MS = 6000;
  const POPUP_SCROLL_RATIO = 0.4;
  const SUBMIT_TIMEOUT_MS = 15000; // a stalled request reaches the native fallback instead of hanging
  const CONSENT_ID = 'shopify-pc__banner';
  const HOLD_POLL_MS = 500;
  const HOLD_GRACE_MS = 1500;
  const CODE = 'KING';
  const SURFACE_HASH = { '#ContactPopup': 'popup', '#ContactSticky': 'sticky' };

  const isDesignMode = () => !!(window.Shopify && window.Shopify.designMode);

  // Stored state may be partial or malformed: keep a default for every field
  // whose stored type is wrong.
  const readState = () => {
    const out = { state: 'idle', dismissedPopupUntil: 0, dismissedSticky: false };
    try {
      const raw = JSON.parse(localStorage.getItem(KEY));
      if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
        if (typeof raw.state === 'string') out.state = raw.state;
        if (Number.isFinite(raw.dismissedPopupUntil)) out.dismissedPopupUntil = raw.dismissedPopupUntil;
        if (typeof raw.dismissedSticky === 'boolean') out.dismissedSticky = raw.dismissedSticky;
      }
    } catch (_) { /* storage unavailable or corrupt: defaults */ }
    return out;
  };

  // Shopify's cookie-consent dialog is a fixed bottom overlay; never stack the popup on it.
  // "Visible" = the element exists and has layout (display:none counts as gone).
  const consentVisible = () => {
    const el = document.getElementById(CONSENT_ID);
    return !!el && el.getClientRects().length > 0;
  };
  // The popup never opens on its own while something else has the visitor's attention: the cookie
  // dialog, a field they are typing in, or an open Dawn overlay (menu drawer, cart drawer, modal;
  // Dawn marks all of them with an overflow-hidden* class on <body>).
  const typing = () => {
    const a = document.activeElement;
    return !!a && a !== document.body && a.matches('input, textarea, select, [contenteditable]');
  };
  const overlayOpen = () => /(^|\s)overflow-hidden/.test(document.body.className);
  const holdPopup = () => consentVisible() || typing() || overlayOpen();
  // Run cb once nothing holds the popup, HOLD_GRACE_MS after the last hold ended (re-checked: the
  // visitor may have started typing during the grace period).
  const whenClear = (cb) => {
    if (!holdPopup()) { cb(); return; }
    const poll = () => (holdPopup() ? setTimeout(poll, HOLD_POLL_MS) : setTimeout(() => whenClear(cb), HOLD_GRACE_MS));
    setTimeout(poll, HOLD_POLL_MS);
  };

  const seenThisVisit = () => {
    try { return sessionStorage.getItem(SEEN_KEY) === '1'; } catch (_) { return false; }
  };
  const markSeen = () => {
    try { sessionStorage.setItem(SEEN_KEY, '1'); } catch (_) { /* ignore */ }
  };

  // Set when this tab submitted a welcome form: a bare ?customer_posted=true in a typed or shared
  // link must not reveal the code (D-02). It is taken by the redirect page, or by the AJAX answer
  // that made a redirect unnecessary. A page in between leaves it alone: Shopify may show its own
  // challenge page before it redirects back.
  const markPosted = () => {
    try { sessionStorage.setItem(POSTED_KEY, '1'); } catch (_) { /* ignore */ }
  };
  const takePosted = () => {
    try {
      const posted = sessionStorage.getItem(POSTED_KEY) === '1';
      sessionStorage.removeItem(POSTED_KEY);
      return posted;
    } catch (_) { return false; }
  };

  Alpine.store('welcome', {
    state: 'idle',
    code: '',
    dismissedPopupUntil: 0,
    dismissedSticky: false,
    popupOpen: false,
    origin: '',
    shownThisVisit: false,

    init() {
      Object.assign(this, readState());
      this.code = ''; // never rehydrate the code
      // Native-POST fallback: Shopify redirected back after a full-page submit from this tab.
      if (new URLSearchParams(window.location.search).get('customer_posted') === 'true' && takePosted()) {
        const kind = SURFACE_HASH[window.location.hash] || '';
        this.subscribe(kind);
        if (kind === 'popup') this.popupOpen = true;
      }
      this.armTriggers();
    },

    persist() {
      const { state, dismissedPopupUntil, dismissedSticky } = this; // never persist code
      try {
        localStorage.setItem(KEY, JSON.stringify({ state, dismissedPopupUntil, dismissedSticky }));
      } catch (_) { /* storage full or unavailable */ }
    },

    get subscribed() { return this.state === 'subscribed'; },
    // May the popup open on its own (trigger engine)?
    get popupEligible() {
      return !this.subscribed && !isDesignMode() && Date.now() > this.dismissedPopupUntil;
    },
    // A surface stays visible after subscribing only on the surface that took the signup,
    // so the visitor sees the reveal; every other surface hides.
    get showPopup() {
      return this.popupOpen && !isDesignMode() && (!this.subscribed || this.origin === 'popup');
    },
    // Sticky only once the popup is suppressed and closed: one interruptive surface at a time.
    get showSticky() {
      return !this.popupOpen && !this.dismissedSticky && Date.now() <= this.dismissedPopupUntil &&
        (!this.subscribed || this.origin === 'sticky');
    },

    subscribe(kind) {
      this.code = CODE;
      this.state = 'subscribed';
      this.origin = kind || '';
      this.persist();
    },
    openPopup() { // explicit user action (sticky tap-through): ignores suppression
      if (this.subscribed) return;
      this.popupOpen = true;
      this.shownThisVisit = true;
    },
    dismissPopup() {
      this.popupOpen = false;
      this.dismissedPopupUntil = Date.now() + POPUP_SUPPRESS_DAYS * DAY_MS;
      this.persist();
    },
    dismissSticky() {
      this.dismissedSticky = true;
      this.persist();
    },
    applyToCart() {
      // Full navigation sets the discount cookie; the Dawn cart drawer is not touched.
      // Keep the visitor on their own locale storefront (root is "/" or "/nl/").
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      window.location.href = root + 'discount/' + CODE + '?redirect=' + root + 'cart';
    },

    // Popup trigger engine: ~6s delay OR ~40% scroll OR desktop exit-intent, once per visit.
    armTriggers() {
      if (!this.popupEligible || seenThisVisit()) return;
      const cleanups = [];
      const open = () => {
        if (this.popupEligible && !this.popupOpen && !this.shownThisVisit) {
          this.popupOpen = true;
          this.shownThisVisit = true;
          markSeen();
        }
      };
      let fired = false;
      const fire = () => {
        if (fired) return;
        fired = true;
        cleanups.forEach((fn) => fn());
        whenClear(open);
      };
      const timer = setTimeout(fire, POPUP_DELAY_MS);
      cleanups.push(() => clearTimeout(timer));

      let ticking = false;
      const onScroll = () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          ticking = false;
          const max = document.documentElement.scrollHeight - window.innerHeight;
          if (max > 0 && window.scrollY / max >= POPUP_SCROLL_RATIO) fire();
        });
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      cleanups.push(() => window.removeEventListener('scroll', onScroll));

      if (window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
        const onLeave = (e) => { if (e.clientY <= 0) fire(); };
        document.documentElement.addEventListener('mouseleave', onLeave);
        cleanups.push(() => document.documentElement.removeEventListener('mouseleave', onLeave));
      }
    },
  });

  // Success detection (the theme's real footer markup): an error is
  // [aria-invalid] / #<formId>-error / .newsletter-form__message without --success.
  // Absence of an error means success (an already-subscribed email returns no error).
  const errorSelector = (form) => [
    '[aria-invalid="true"]',
    '.newsletter-form__message:not(.newsletter-form__message--success)',
    form.id ? '#' + CSS.escape(form.id + '-error') : '',
  ].filter(Boolean).join(',');

  Alpine.data('kpWelcomeSurface', (kind) => ({
    kind,
    busy: false,
    error: false, // per surface: an error on one form is not shown on the others
    native: false, // set once the AJAX attempt failed: the next send() is a real navigation

    init() {
      // Must run before the visitor focuses the field: Shopify binds a form on first focus
      // and captures form.submit at that moment, so its token wrapper ends in send().
      this.$el.querySelectorAll('form.kp-welcome-form').forEach((form) => {
        // Bound before Alpine started: keep Shopify's wrapper. It posts natively with a token
        // and the store's init() reveals the code after the reload.
        if (!form.hasAttribute('data-hcaptcha-bound')) form.submit = () => this.send(form);
      });
    },

    // The visitor pressed the submit button (bound with @submit, no .prevent).
    submit(ev) {
      const form = ev.target.closest('form');
      if (!form) return;
      ev.preventDefault();
      markPosted();
      // Bound: Shopify's own listener on the form already requested a token and will call
      // form.submit(), which ends in send().
      if (form.hasAttribute('data-hcaptcha-bound')) return;
      const captcha = window.Shopify && window.Shopify.captcha;
      if (form.dataset.nocaptcha !== 'true' && captcha && captcha.protect) {
        captcha.protect(form, () => form.submit()); // bind, then token, then send()
      } else {
        this.send(form); // no captcha on the store, or the form opted out
      }
    },

    // Runs with the captcha token already in the form (or on a form that needs none).
    async send(form) {
      const store = Alpine.store('welcome');
      if (this.native) {
        HTMLFormElement.prototype.submit.call(form); // real navigation with a fresh token
        return;
      }
      if (this.busy) return;
      this.busy = true;
      this.error = false;
      try {
        const res = await fetch(form.action, {
          method: 'POST',
          headers: { Accept: 'text/html' },
          body: new URLSearchParams(new FormData(form)),
          signal: AbortSignal.timeout ? AbortSignal.timeout(SUBMIT_TIMEOUT_MS) : undefined,
        });
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        const hasError = !!doc.querySelector(errorSelector(form));
        const recognised = hasError ||
          res.url.indexOf('customer_posted=true') !== -1 ||
          !!doc.querySelector('.newsletter-form__message--success');
        if (!res.ok || !recognised) throw new Error('unrecognised customer form response');
        takePosted(); // settled here: no redirect page will follow
        if (hasError) this.error = true; else store.subscribe(this.kind);
      } catch (_) {
        // Never drop the capture. Go through form.submit() again so a bound form gets a
        // FRESH token (tokens are single-use); send() then takes the native branch above.
        // Shopify redirects back with ?customer_posted=true, which the store's init() reveals.
        this.native = true;
        form.submit();
        return;
      }
      this.busy = false;
    },
  }));
});
